import React from "react"
import EChartsReact from "@/components/EChartsReact"
import moment from "moment"

/**
 * Consumption and Costs 基础图表组件
 * 提供最基础的ECharts功能，接收原始数据作为props
 * 
 * @param {Object} props
 * @param {Array} props.data - 数据数组，格式：[{month: 'YYYY-MM', usage: number, costs: number}]
 * @param {string} props.type - 数据类型，'electricity' 或 'gas'
 * @param {string} props.title - 图表标题
 * @param {string} props.className - CSS类名
 * @param {Object} props.style - 样式对象
 * @param {Object} props.customOptions - 自定义ECharts配置选项（可选）
 */
export const UsageAndCostsBaseChart = ({ 
  data = [], 
  type = 'electricity',
  title = '',
  className = '',
  style = { width: '100%', height: '100%' },
  customOptions = {}
}) => {
  
  // 配置信息
  const config = {
    gas: {
      title: 'Monthly Gas Usage and Costs',
      name: 'Gas',
      color: '#c00000',
      unit: 'MMbtu'
    },
    electricity: {
      title: 'Monthly Electricity Usage and Costs',
      name: 'Electricity',
      color: '#4472c4',
      unit: 'kWh'
    }
  };

  const currentConfig = config[type];
  const displayTitle = title || currentConfig.title;
  
  // 基础ECharts配置
  const baseOption = {
    backgroundColor: '#fff',
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const date = moment(params[0].axisValue, "YYYY-M").format('MMM YYYY');
        let result = `${date}<br/>`;
        
        params.forEach(param => {
          const value = param.value;
          const marker = param.marker;
          const seriesName = param.seriesName;
          let formattedValue;
          
          if (seriesName === 'Costs') {
            formattedValue = `$ ${Math.round(value).toLocaleString()}`;
          } else {
            // Usage formatting
            if (type === 'gas') {
              formattedValue = `${Math.round(value).toLocaleString()} ${currentConfig.unit}`;
            } else {
              formattedValue = `${Math.round(value).toLocaleString()} ${currentConfig.unit}`;
            }
          }
          
          result += `${marker}${seriesName}: ${formattedValue}<br/>`;
        });
        
        return result;
      }
    },
    xAxis: [
      {
        type: 'category',
        data: data.map(item => item.month),
        axisLabel: {
          rotate: 50,
          formatter: function (value) {
            return moment(value, "YYYY-M").format("MMM");
          }
        },
        position: 'bottom'
      },
      {
        type: 'category',
        position: 'top',
        axisLine: {
          show: true
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          formatter: function (value) {
            return moment(value, "YYYY-M").format("YYYY");
          }
        },
        data: data.map(item => item.month)
      }
    ],
    yAxis: [
      {
        type: 'value',
        name: currentConfig.unit,
        position: 'left',
        nameLocation: 'middle',
        nameGap: 38,
        axisLabel: {
          formatter: function (value) {
            if(type === 'gas') {
              return value >= 1000 ? (value / 1000).toFixed(1) + 'K' : parseInt(value);
            }
            return value >= 1000 ? (value / 1000).toFixed(type === 'gas' ? 1 : 0) + 'K' : parseInt(value);
          },
        },
      },
      {
        type: 'value',
        name: '',
        position: 'right',
        splitLine: {
            show: false
          },
        axisLabel: {
          formatter: function (value) {
            return '$' + (value >= 1000 ? (value / 1000).toFixed(0) + 'K' : parseInt(value));
          }
        }
      }
    ],
    series: [
      {
        name: 'Usage',
        type: 'bar',
        data: data.map(item => item.usage),
        itemStyle: {
          color: currentConfig.color,
          borderRadius: [6, 6, 0, 0]
        }
      },
      {
        name: 'Costs',
        type: 'line',
        yAxisIndex: 1,
        data: data.map(item => item.costs),
        itemStyle: {
          color: '#70ad47'
        }
      }
    ],
    title: {
      text: displayTitle,
      left: 'center',
      top: '2%',
      textStyle: {
        color: '#396598',
        fontSize: 14
      }
    },
    legend: {
      data: ['Usage', 'Costs'],
      left: 'center',
      top: '12%',
      itemWidth: 10,
      itemHeight: 10
    },
    grid: {
      top: '20%',
      left: '4%',
      right: '4%',
      bottom: '4%',
      containLabel: true
    },
  };

  // 合并自定义配置
  const finalOption = { ...baseOption, ...customOptions };

  return (
    <EChartsReact 
      className={className} 
      option={finalOption} 
      style={style} 
    />
  );
};
